/**
 * Studios for the views (PLAN §5.1, D22): the list, one studio with its
 * config, create and save. Query/mutation option factories over the API
 * client (the data-hook pattern, `rundowns.ts`).
 *
 * **Optimistic concurrency.** A save names the version it was made from; a
 * studio saved by someone else meanwhile answers `conflict` (409), which
 * `saveStudioMutation` turns into a `StudioConflict` carrying the studio as
 * it now is — the view offers to load it or to put the user's edit on top.
 */
import { ApiError, type ApiClient } from '@acp/api-client/client.js';
import type { Studio, StudioSummary } from '@acp/api-contract/studios.js';
import type { JsonValue } from '@acp/domain/primitives.js';
import { type QueryClient, queryOptions } from '@tanstack/react-query';

export const studioKeys = {
  all: ['studios'] as const,
  list: () => [...studioKeys.all, 'list'] as const,
  one: (studioId: string) => [...studioKeys.all, 'one', studioId] as const,
};

/** Every studio the caller may read, by name. */
export function studiosQuery(api: ApiClient) {
  return queryOptions({
    queryKey: studioKeys.list(),
    queryFn: async (): Promise<StudioSummary[]> => {
      const res = await api.call('listStudios', { params: null, query: null, body: null });
      return [...res.studios].sort((a, b) => a.name.localeCompare(b.name) || a.studioId.localeCompare(b.studioId));
    },
  });
}

export function studioQuery(api: ApiClient, studioId: string) {
  return queryOptions({
    queryKey: studioKeys.one(studioId),
    queryFn: async (): Promise<Studio> => (await api.call('getStudio', { params: { studioId }, query: null, body: null })).studio,
  });
}

export type NewStudio = {
  /** Null: the server mints one. */
  readonly studioId: string | null;
  readonly name: string;
  readonly siteId: string | null;
  /** The show style its productions get unless they name one (D32); null for none. */
  readonly defaultShowStyleId: string | null;
  readonly config: { readonly [key: string]: JsonValue };
};

export function createStudioMutation(api: ApiClient, queryClient: QueryClient) {
  return {
    mutationFn: async (input: NewStudio): Promise<Studio> => {
      const body = { ...(input.studioId === null ? {} : { studioId: input.studioId }), name: input.name, siteId: input.siteId, defaultShowStyleId: input.defaultShowStyleId, config: { ...input.config } };
      return (await api.call('createStudio', { params: null, query: null, body })).studio;
    },
    onSuccess: async (studio: Studio): Promise<void> => {
      queryClient.setQueryData(studioKeys.one(studio.studioId), studio);
      await queryClient.invalidateQueries({ queryKey: studioKeys.list() });
    },
  };
}

/** A save refused because the studio moved on: `current` is the studio as it now is. */
export class StudioConflict extends Error {
  readonly current: Studio;
  constructor(current: Studio) {
    super(`conflict: ${current.updatedBy} saved version ${String(current.version)} meanwhile`);
    this.name = 'StudioConflict';
    this.current = current;
  }
}

export type StudioSave = {
  readonly studioId: string;
  /** The version the edit was made from. */
  readonly version: number;
  readonly name: string;
  readonly siteId: string | null;
  /** The default show style (D32); null for none. */
  readonly defaultShowStyleId: string | null;
  readonly config: { readonly [key: string]: JsonValue };
};

export function saveStudioMutation(api: ApiClient, queryClient: QueryClient) {
  return {
    mutationFn: async (save: StudioSave): Promise<Studio> => {
      try {
        const body = { version: save.version, name: save.name, siteId: save.siteId, defaultShowStyleId: save.defaultShowStyleId, config: { ...save.config } };
        return (await api.call('updateStudio', { params: { studioId: save.studioId }, query: null, body })).studio;
      } catch (err) {
        if (!(err instanceof ApiError) || err.code !== 'conflict') throw err;
        // Re-read what won, so the view can offer it.
        throw new StudioConflict((await api.call('getStudio', { params: { studioId: save.studioId }, query: null, body: null })).studio);
      }
    },
    onSuccess: async (studio: Studio): Promise<void> => {
      queryClient.setQueryData(studioKeys.one(studio.studioId), studio);
      await queryClient.invalidateQueries({ queryKey: studioKeys.list() });
    },
  };
}
